import { message } from 'antd';
import { RcFile } from 'antd/es/upload';
import { IFile } from '@/types/fileImport';
import { MONO_CHANNEL_THRESHOLD } from "@/constants/fileImport";
import fridayReporter from '@/utils/friday';
import { loadFFmpeg, getAudioFileInfo, convertMonoMp3File, convertMp3File } from './utils/AudioProcessor';
import { validateAudioDuration } from './utils/AudioValidator';
import { filesImport } from './index';

/**
 * 音频导入业务层服务
 * 负责音频文件处理、验证、导入等业务逻辑
 */

// 音频导入相关状态（模块级变量）
const stopFileRead: Map<string, boolean> = new Map(); // 存储是否取消读取的文件映射

/**
 * 初始化音频导入服务
 */
export async function initAudioImport() {
  await loadFFmpeg();
  stopFileRead.clear();
}

/**
 * 添加取消读取的文件
 * @param uid 上传的文件唯一标识
 */
export function addStopFileRead(uid: string): void {
  stopFileRead.set(uid, false);
}

/**
 * 处理音频文件的核心逻辑
 * @param file 待处理的文件
 * @param uid 文件唯一标识
 * @param onFileUpdated 文件更新后的回调，返回更新后的文件信息
 * @param onError 处理失败的回调
 * @returns 如果处理成功返回 true，失败返回 false
 */
export async function processAudioFile(
  file: File | RcFile,
  uid: string,
  onFileUpdated: (updatedFile: IFile) => void,
  onError?: () => void
): Promise<boolean> {
  try {
    // 检查文件是否已被取消
    if (!stopFileRead.has(uid)) {
      console.log('文件处理完成之前已经删除，直接返回');
      return false;
    }

    // 获取音频文件信息
    const fileInfo = await getAudioFileInfo(file, uid);
    const { duration, channels } = fileInfo;
    console.log('fileInfo', file, fileInfo);

    // 再次检查文件是否已被取消
    if (!stopFileRead.has(uid)) {
      console.log('文件处理完成之前已经删除，直接返回');
      return false;
    }

    console.log(`音频文件的声道 = ${channels} 时长=${duration}`);

    // 检查时长限制
    if (!validateAudioDuration(duration)) {
      stopFileRead.delete(uid);
      message.warning('单个文件时长不超过3小时');
      onError?.();
      return false;
    }

    // 如果是多声道，转换为单声道
    if (channels > MONO_CHANNEL_THRESHOLD) {
      console.log('转换为单声道');
      const monoFile = await convertMonoMp3File(file, uid);
      console.log('convertMonoFile', monoFile);
      // 递归处理转换后的文件
      return await processAudioFile(monoFile, uid, onFileUpdated, onError);
    }

    // 转换为 MP3 格式（cstore 对某些音频格式不支持）
    const mp3File = await convertMp3File(file, uid);

    // 创建更新后的文件信息
    const updatedFile: IFile = {
      file: mp3File as RcFile,
      uid,
      inProcess: false,
      duration,
      size: mp3File.size
    };

    console.log('文件处理完成', updatedFile);
    onFileUpdated(updatedFile);
    return true;
  } catch (error) {
    // 检查文件是否已被取消
    if (!stopFileRead.has(uid)) {
      console.log('文件处理完成之前已经删除，直接返回');
      return false;
    }
    console.log('音频文件处理出错', error);
    stopFileRead.delete(uid);
    message.error('文件处理错误，请检查文件格式');
    onError?.();
    return false;
  }
}

/**
 * 删除音频文件（清理资源）
 * @param uid 文件唯一标识
 */
export function deleteAudioFile(uid: string): void {
  // 标记文件为已取消
  if (stopFileRead.has(uid)) {
    stopFileRead.delete(uid);
  }
}

/**
 * 检查是否可以开始导入
 * @param fileList 文件列表
 * @returns 是否可以开始导入
 */
export function checkCanStartAudioImport(fileList: IFile[]): boolean {
  if (fileList.length === 0) {
    return false;
  }

  // 检查是否有文件正在处理中
  return !fileList.some(file => file.inProcess);
}

/**
 * 开始导入音频文件
 * @param fileList 文件列表
 */
export function startAudioImport(fileList: IFile[]): void {
  if (fileList.length <= 0) {
    message.warning('请选择文件');
    return;
  }

  if (!checkCanStartAudioImport(fileList)) {
    message.warning('文件正在处理中，请稍后');
    return;
  }

  // 上报事件
  fridayReporter.pushData({
    eventCode: 'HomePage_import_start'
  });

  filesImport(fileList);
}

/**
 * 关闭导入时的清理工作
 * @param fileList 文件列表
 */
export function closeAudioImport(): void {
  stopFileRead.clear();
}